import React from "react";
import { RenderedComponent, useTargetWizardContext } from "../context/target-wizard-context";
import { WizardStepOne } from "../financial-target-wizard/wizard-step-one";
import { List, Map } from "immutable";
import { Button, Dialog, DialogType, Loading } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import "./financial-target-form.css";
import { AddTargetValuesContainer } from "../left-side/add-target-values/add-target-values-container";
import {
    SaveTargetObjectsPayload,
    TargetsQueueType,
    saveTargetObjectsAction,
} from "actions/collections/collection-target";
import { connect } from "react-redux";
import { Metric } from "helpers/metrics/types";
import { GridApi } from "ag-grid-community";
import { IStore } from "reducers";
import { getAllocationDimensionsBySubtype } from "reducers/selectors/collection";
import { AllocationDimensionKind, IAllocationDimension } from "helpers/allocation-dimension";
import { ICollectionState } from "reducers/collection";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { CollectionKeys, TARGET_GROUPS_ID } from "constants/product-collection";
import { withRouter } from "react-router";
import { parseMetricSchema } from "reducers/dimensions-panel/draft";

export const FINANCIAL_TARGET_FORM_ID = "financial_target_form";

interface Props {
    merchantId: string;
    schema: List<Map<string, any>>;
}

interface ReduxProps {
    metrics: Metric[];
    gridApi: GridApi;
    genericAllocationDimensions: IAllocationDimension[];
    isEditing: boolean;
    isSavingTarget: boolean;
    targetGroups: List<Map<string, any>>;
}

export const FinancialTargetFormComponent = ({
    schema,
    merchantId,
    metrics,
    gridApi,
    genericAllocationDimensions,
    saveTargetObjects,
    isEditing,
    isSavingTarget,
    targetGroups,
}: Props & ReduxProps & DispatchActions) => {
    const wizardStepOneRef = React.useRef(null);
    const savingTargetFlag = React.useRef(false);

    const {
        steps: {
            currentStep,
            helpers: { goToNextStep, goToPrevStep },
        },
        renderedComponent: [renderedComponent, setRenderedComponent],
        targetGroupWithTargetsSnapshot: [targetGroupsWithTargetsSnapshot, setTargetGroupWithTargetsSnapshot],
        targetGroupFormIsValid: [targetGroupFormIsValid],
        targetAllocationBagBuffer: [targetAllocationBagBuffer],
        targetAllocationBagBufferIsValid: [targetAllocationBagBufferIsValid],
    } = useTargetWizardContext();

    const handleNextStep = () => {
        if (currentStep === 1 && wizardStepOneRef.current) {
            const updatedSnapshot = wizardStepOneRef.current.updateFormToSnapshot();
            setTargetGroupWithTargetsSnapshot(updatedSnapshot);
            goToNextStep();

            return;
        } else if (currentStep === 2 && targetAllocationBagBufferIsValid) {
            saveTargetObjects({
                targetGroupWithTargets: targetGroupsWithTargetsSnapshot,
                targetBag: targetAllocationBagBuffer,
                queueOptions: {
                    targetName: targetGroupsWithTargetsSnapshot.name,
                },
            });
            return;
        }
    };

    const handlePrevStep = () => {
        if (currentStep === 1) {
            closeDialog();
            return;
        }
        goToPrevStep();
    };

    const onDismissDialog = (e: React.MouseEvent | KeyboardEvent) => {
        // Don't close on escape. Annoying UX
        if ((e as KeyboardEvent).key === KEYBOARD_KEYS.ESCAPE) {
            return;
        }
        closeDialog();
    };

    const closeDialog = React.useCallback(() => {
        setRenderedComponent(RenderedComponent.MANAGE_TARGETS_DIALOG);
    }, [setRenderedComponent]);

    const messageByStep = {
        1: {
            nextBtnMsg: formatMessage("Next"),
            previousBtnMsg: formatMessage("Cancel"),
        },
        2: {
            nextBtnMsg: formatMessage("Finish"),
            previousBtnMsg: formatMessage("Back"),
        },
    };

    const isNextButtonEnabled = React.useMemo(() => {
        if (currentStep === 1) {
            return targetGroupFormIsValid;
        } else if (currentStep === 2) {
            return targetAllocationBagBufferIsValid;
        }
        return false;
    }, [currentStep, targetGroupFormIsValid, targetAllocationBagBufferIsValid]);

    const dialogTitle = isEditing ? "Edit target" : "Create new target";

    React.useEffect(() => {
        if (isSavingTarget) {
            savingTargetFlag.current = true;
        }

        if (savingTargetFlag.current && !isSavingTarget) {
            closeDialog();
        }
    }, [isSavingTarget, closeDialog]);

    return (
        renderedComponent === RenderedComponent.FINANCIAL_TARGET_FORM_DIALOG && (
            <Dialog
                dialogContentProps={{
                    type: DialogType.normal,
                    title: formatMessage(dialogTitle),
                    showCloseButton: true,
                }}
                hidden={false}
                onDismiss={onDismissDialog}
                modalProps={{
                    containerClassName: "financial-target-form-dialog-container",
                }}
            >
                <div className="financial-target-form-dialog-body" id={FINANCIAL_TARGET_FORM_ID}>
                    {isSavingTarget ? (
                        <div
                            style={{
                                display: "flex",
                                alignContent: "center",
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            <Loading />
                        </div>
                    ) : (
                        <>
                            {currentStep === 1 && (
                                <WizardStepOne
                                    schema={schema}
                                    merchantId={merchantId}
                                    metrics={metrics}
                                    gridApi={gridApi}
                                    genericAllocationDimensions={genericAllocationDimensions}
                                    ref={wizardStepOneRef}
                                    targetGroups={targetGroups}
                                />
                            )}
                            {currentStep === 2 && <AddTargetValuesContainer />}
                        </>
                    )}
                </div>
                <div className="financial-target-form-dialog-footer">
                    <div style={{ display: "flex", columnGap: "16px" }}>
                        {/* <Button onClick={createTargetGroup}>SAVE</Button> */}
                        <Button
                            disabled={isSavingTarget}
                            className="financial-target-form-button"
                            onClick={handlePrevStep}
                        >
                            {messageByStep[currentStep].previousBtnMsg}
                        </Button>
                        <Button
                            className="financial-target-form-button primary"
                            primary
                            disabled={!isNextButtonEnabled || isSavingTarget}
                            onClick={handleNextStep}
                        >
                            {messageByStep[currentStep].nextBtnMsg}
                        </Button>
                    </div>
                </div>
            </Dialog>
        )
    );
};

const mapStateToProps = (state: IStore, ownProps) => {
    const { collectionId, targetsQueue } = state.collection as ICollectionState;
    const collection = state.mutate.snapshots && state.mutate.snapshots.get(collectionId);
    const metrics = collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.DOOR_GROUP_METRICS);
    const parsedMetrics = parseMetricSchema(metrics.toJS());

    const gridApi = state.collection.gridApi;
    const isEditing = targetsQueue.type === TargetsQueueType.EDIT;
    const targetGroups = state.mutate.snapshots.get(TARGET_GROUPS_ID);
    const genericAllocationDimensions = getAllocationDimensionsBySubtype(
        state,
        collectionId,
        AllocationDimensionKind.Generic
    );
    const isSavingTarget = Boolean(targetsQueue.targetName);

    return { metrics: parsedMetrics, gridApi, genericAllocationDimensions, isEditing, isSavingTarget, targetGroups };
};

const mapDispatchToProps = (dispatch) => {
    const saveTargetObjects = (payload: SaveTargetObjectsPayload) => dispatch(saveTargetObjectsAction(payload));
    return { saveTargetObjects };
};

type DispatchActions = {
    saveTargetObjects: (payload: SaveTargetObjectsPayload) => void;
};

export const FinancialTargetForm = withRouter(
    connect<ReduxProps, DispatchActions, Props>(mapStateToProps, mapDispatchToProps)(FinancialTargetFormComponent)
);
